import Icon, { DesktopOutlined, EllipsisOutlined, NotificationOutlined, PictureOutlined, StarOutlined } from '@ant-design/icons'
import { RunningContentContentTypeEnum, ScreenModel } from '@hmedia/legenda-ds-api-client'
import { Badge, Button, Dropdown, Image, Space, Tooltip, Typography } from 'antd'
import { FC } from 'react'
import styles from './css.module.css'
import { useWakeUpScreenByMACMutation } from './queries'
import { usePermission } from 'services/security/permissionContext'
import { getGateLabel } from '@hmedia/legenda-ds-common'

const { Title, Text } = Typography

type ListProps = {
  items: ScreenModel[]
  onNameChange?: (id: string, name: string) => void
  onMenuClick?: (screen: ScreenModel) => void
  onGateAssignClick?: (screen: ScreenModel) => void
  onCustomContentPermissionClick?: (screen: ScreenModel) => void
}

const ScreenItemList: FC<ListProps> = (props) => {
  let { items, onNameChange, onMenuClick, onGateAssignClick, onCustomContentPermissionClick } = props

  return (
    <>
      {items.map((item) => {
        return (
          <ScreenItem
            onMenuClick={onMenuClick}
            onGateAssignClick={onGateAssignClick}
            onCustomContentPermissionClick={onCustomContentPermissionClick}
            key={item.id}
            screen={item}
            onNameChange={onNameChange}
          />
        )
      })}
    </>
  )
}

type ItemProps = {
  screen: ScreenModel
  onNameChange?: (id: string, name: string) => void
  onMenuClick?: (screen: ScreenModel) => void
  onGateAssignClick?: (screen: ScreenModel) => void
  onCustomContentPermissionClick?: (screen: ScreenModel) => void
}

const ScreenItem: FC<ItemProps> = (props) => {
  const { screen, onNameChange, onMenuClick, onGateAssignClick, onCustomContentPermissionClick } = props
  const isAllowedToAdminAndSUOnly = usePermission(['SUPERUSER', 'ROLE_ADMIN'])

  // Temporarily extend the ScreenModel to include customContentEnabled
  const extendedScreen = screen as ScreenModel & { customContentEnabled?: boolean }

  let filtered = Object.keys(screen.runningContent).filter((key) => key !== 'contentType')
  const contentTitle =
    (screen.runningContent.contentType === RunningContentContentTypeEnum.MEDIAFILE &&
      screen.runningContent?.mediaFileInstance?.title) ||
    filtered
      .map((sc) => screen.runningContent[sc]?.['meta']?.['title'])
      .filter((sc) => sc !== undefined)[0]

  const thumbnailUrl =
    (screen.runningContent.contentType === RunningContentContentTypeEnum.MEDIAFILE &&
      screen.runningContent?.mediaFileInstance?.thumbnailURL) ||
    Object.keys(screen.runningContent)
      .map((key) => screen.runningContent[key]?.['meta']?.['thumnailURL'])
      .filter((sc) => sc !== undefined)[0]

  const runningActuality = screen.actuality?.contentMap?.[screen.contentType]

  const menuItems = [
    {
      key: 'setcontent',
      onClick: () => {
        onMenuClick(screen)
      },
      label: 'Tartalom beállítása',
    },
    ...(isAllowedToAdminAndSUOnly ? [{
      key: 'assigngate',
      label: 'GATE hozzárendelése',
      onClick: () => {
        onGateAssignClick?.(screen)
      },
    }] : []),
    ...(isAllowedToAdminAndSUOnly ? [{
      key: 'customcontentpermission',
      label: 'Egyedi tartalom engedélye',
      onClick: () => {
        onCustomContentPermissionClick?.(screen)
      },
    }] : []),
  ]

  return (
    <div className={styles.screenItem}>
      {(thumbnailUrl && <Image src={thumbnailUrl} height={60} />) || (
        <DesktopOutlined
          style={{ fontSize: '24px' }}
          className={screen.connected ? styles.statusColorOnline : styles.statusColorOffline}
        />
      )}
      <Space direction="vertical" size={0} style={{ minWidth: '350px' }}>
        <Title
          level={4}
          editable={{
            onChange: (name: string) => onNameChange && onNameChange(screen.id, name),
            tooltip: false,
          }}
        >
          {screen.name}
        </Title>
        <span>Lejátszás alatt: {contentTitle || '-'}</span>
      </Space>
      <Space direction="vertical" size={'small'}>
        {screen.info && (
          <Space direction="horizontal" className={styles.infoContent}>
            <Icon component={NotificationOutlined} />
            Futó infó:
            <Text>{screen.info?.name}</Text>
          </Space>
        )}
        {runningActuality && (
          <Space direction="horizontal" className={styles.infoContent}>
            <Icon component={StarOutlined} />
            Futó aktualitás:
            <Text>{screen.actuality?.name}</Text>
          </Space>
        )}
      </Space>
      <Space direction="vertical" size="small" className={styles.macModelNumber}>
        <div>
          MAC cím: <Text copyable={true}>{screen.device?.id}</Text>
        </div>
      </Space>
      {screen.gate &&  (
        <div className={styles.gateText}>
        GATE: <Text>{getGateLabel(screen.gate)}</Text>
      </div>
      )}
      {isAllowedToAdminAndSUOnly && (screen.customContentEnabled === false) && (
        <div className=''>
          <Tooltip overlay={'Az egyedi tartalom megjelenítése le van tiltva.'} placement="top">
            <PictureOutlined style={{ fontSize: '18px', color: 'red' }} />
          </Tooltip>
        </div>
      )}

      <Badge status={screen.connected ? 'success' : 'error'} text={screen.status} />
      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
        <Button icon={<EllipsisOutlined />} shape="circle" />
      </Dropdown>
    </div>
  )
}
export default ScreenItemList
